import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { format, humanizeStatus } from "@/lib/brand-tokens";
import { getReservationsByMember, getDealById, getBrandById, DEMO_MEMBER } from "@/lib/mock-data";

// MEM-04 — My reservations
const STATUS_TABS = [
  { id: "all",       label: "All" },
  { id: "Active",    label: "Active" },
  { id: "Submitted", label: "Submitted" },
  { id: "Completed", label: "Completed" },
];

export default function MyReservationsPage() {
  const reservations = getReservationsByMember(DEMO_MEMBER.id);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    return reservations.filter((r) => {
      if (tab === "Active" && r.status !== "Active" && r.status !== "SubmittedPartial") return false;
      if (tab === "Submitted" && r.status !== "SubmittedPartial" && r.status !== "SubmittedFinal") return false;
      if (tab === "Completed" && r.status !== "SubmittedFinal" && r.status !== "ExpiredReleased" && r.status !== "Cancelled") return false;
      if (q) {
        const deal = getDealById(r.dealId);
        // Use the reservation's specific brand (set when reserved) over the
        // deal's primary brand — a multi-brand deal has one reservation per brand.
        const brand = getBrandById(r.brandId || deal?.brandId);
        const haystack = `${brand?.name} ${deal?.name}`.toLowerCase();
        if (!haystack.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [reservations, tab, q]);

  return (
    <div data-testid="my-reservations-page" className="space-y-6 pb-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Your reservations</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">My reservations</h1>
          <p className="text-body-sm text-ink-muted mt-1">{reservations.length} total · {filtered.length} matching</p>
        </div>
        <Link to="/member/deals">
          <Button className="bg-brand-700 hover:bg-brand-800 text-white">+ New reservation</Button>
        </Link>
      </div>

      <div className="bg-surface border border-line rounded-xl">
        <div className="p-4 border-b border-line flex flex-wrap items-center gap-3">
          <div className="flex bg-surface-alt rounded-md p-0.5">
            {STATUS_TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                      className={`px-3 py-1.5 rounded-md text-caption font-semibold transition-all ${
                        tab === t.id ? "bg-surface text-ink shadow-xs" : "text-ink-muted hover:text-ink"
                      }`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by brand or deal…" className="pl-8 h-9" />
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            title="No reservations match"
            description={reservations.length === 0 ? "You haven't reserved anything yet. Browse live deals to get started." : "Try a different filter or search term."}
            action={reservations.length === 0 ? "Browse deals" : null}
            onAction={reservations.length === 0 ? () => window.location.assign("/member/deals") : null}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-alt">
                <tr className="text-left">
                  <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Brand / Deal</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Offered</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Submitted</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Cards</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Expected payout</th>
                  <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Deadline</th>
                  <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {filtered.map((r) => {
                  const deal = getDealById(r.dealId);
                  // Each reservation is scoped to one specific brand even on
                  // multi-brand deals — use that, not the deal's primary brand.
                  const brand = getBrandById(r.brandId || deal?.brandId);
                  const isActive = r.status === "Active" || r.status === "SubmittedPartial";
                  return (
                    <tr key={r.id} className="hover:bg-surface-alt/40">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <BrandLogo brand={brand} size="sm" />
                          <div className="min-w-0">
                            <div className="text-ink font-semibold truncate max-w-[280px]">{deal?.name}</div>
                            <div className="text-caption text-ink-subtle truncate max-w-[280px]">{brand?.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4"><StatusBadge entity="reservation" status={r.status} dot /></td>
                      <td className="px-3 py-4 text-ink font-semibold tabular-nums text-right">{format.currency(r.faceValueReserved)}</td>
                      <td className="px-3 py-4 text-ink tabular-nums text-right">{format.currency(r.faceValueSubmitted || 0)}</td>
                      <td className="px-3 py-4 text-ink tabular-nums text-right">{r.cardsApproved} / {r.cardsSubmitted}</td>
                      <td className="px-3 py-4 text-brand-700 font-semibold tabular-nums text-right">{format.currency(r.expectedPayout)}</td>
                      <td className="px-3 py-4 text-ink-muted text-caption tabular-nums">{deal?.deadline ? format.date(deal.deadline) : "—"}</td>
                      <td className="px-6 py-4 text-right">
                        <Link to={`/member/reservations/${r.id}`}
                              className={`inline-flex items-center gap-1 text-caption font-semibold ${isActive ? "text-brand-700 hover:text-brand-800" : "text-ink-muted hover:text-ink"}`}>
                          {isActive ? "Submit cards" : "View"}
                          <ChevronRight className="size-3.5" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
